import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Upload } from "lucide-react";
import { toast } from "sonner";

function Field({ field, value, onChange }) {
  const base =
    "w-full rounded-[12px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] px-4 py-3 text-[14px] text-white placeholder:text-white/35 focus:border-white/30 focus:outline-none";

  if (field.type === "textarea") {
    return (
      <textarea
        rows={4}
        value={value || ""}
        placeholder={field.placeholder}
        data-testid={`field-${field.name}`}
        onChange={(e) => onChange(field.name, e.target.value)}
        className={base}
      />
    );
  }

  if (field.type === "select") {
    return (
      <select
        value={value || ""}
        data-testid={`field-${field.name}`}
        onChange={(e) => onChange(field.name, e.target.value)}
        className={`${base} appearance-none`}
      >
        <option value="" className="bg-[#0A0A0A]">{field.placeholder || "Select an option"}</option>
        {field.options.map((o) => (
          <option key={o} value={o} className="bg-[#0A0A0A]">{o}</option>
        ))}
      </select>
    );
  }

  if (field.type === "radio") {
    return (
      <div className="flex flex-wrap gap-2.5" data-testid={`field-${field.name}`}>
        {field.options.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onChange(field.name, o)}
            className={`rounded-[12px] border px-4 py-2.5 text-[13px] font-medium transition-colors ${
              value === o ? "border-white bg-white text-black" : "border-[color:var(--acx-border)] text-white/75 hover:border-white/30"
            }`}
          >
            {o}
          </button>
        ))}
      </div>
    );
  }

  if (field.type === "checkbox") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => {
      const next = arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o];
      onChange(field.name, next);
    };
    return (
      <div className="flex flex-wrap gap-2.5" data-testid={`field-${field.name}`}>
        {field.options.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => toggle(o)}
            className={`inline-flex items-center gap-2 rounded-[12px] border px-4 py-2.5 text-[13px] font-medium transition-colors ${
              arr.includes(o) ? "border-white bg-white text-black" : "border-[color:var(--acx-border)] text-white/75 hover:border-white/30"
            }`}
          >
            {arr.includes(o) && <Check size={14} />} {o}
          </button>
        ))}
      </div>
    );
  }

  if (field.type === "file") {
    return (
      <label className="flex cursor-pointer items-center gap-3 rounded-[12px] border border-dashed border-[color:var(--acx-border)] bg-[var(--acx-card)] px-4 py-4 text-[14px] text-white/70 transition-colors hover:border-white/30">
        <Upload size={18} />
        <span>{value ? value.name : "Upload business document (PDF, JPG, PNG)"}</span>
        <input
          type="file"
          className="hidden"
          data-testid={`field-${field.name}`}
          onChange={(e) => onChange(field.name, e.target.files[0])}
        />
      </label>
    );
  }

  return (
    <input
      type={field.type || "text"}
      value={value || ""}
      placeholder={field.placeholder}
      data-testid={`field-${field.name}`}
      onChange={(e) => onChange(field.name, e.target.value)}
      className={base}
    />
  );
}

export const MultiStepForm = ({ steps, onSubmit, submitLabel = "Submit", testId = "multistep-form", accent = "#E11B22" }) => {
  const [step, setStep] = useState(0);
  const [values, setValues] = useState({});
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const total = steps.length;
  const current = steps[step];
  const progress = ((step + 1) / total) * 100;

  const onChange = (name, val) => {
    if (val instanceof File || val === undefined) {
      setFile(val);
      setValues((p) => ({ ...p, [name]: val ? val.name : "" }));
      return;
    }
    setValues((p) => ({ ...p, [name]: val }));
  };

  const validateStep = () => {
    for (const f of current.fields) {
      if (f.required) {
        const v = values[f.name];
        if (!v || (Array.isArray(v) && v.length === 0)) {
          toast.error(`Please complete: ${f.label}`);
          return false;
        }
        if (f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
          toast.error("Please enter a valid email address");
          return false;
        }
      }
    }
    return true;
  };

  const next = () => {
    if (!validateStep()) return;
    setStep((s) => Math.min(total - 1, s + 1));
    window.scrollTo({ top: window.scrollY > 400 ? 200 : 0, behavior: "smooth" });
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      await onSubmit(values, file);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-[20px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-10 text-center" data-testid={`${testId}-success`}>
        <span className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#9CA3AF]/15 text-[#9CA3AF]">
          <Check size={30} />
        </span>
        <h3 className="mt-5 font-display text-2xl font-bold">Application submitted!</h3>
        <p className="mx-auto mt-3 max-w-md text-[15px] text-white/60">
          Thank you for your interest in AutoCareX. Our team will review your application and reach out to you shortly.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-[20px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-6 sm:p-8" data-testid={testId}>
      {/* Progress */}
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between text-[12px] text-white/50">
          <span>Step {step + 1} of {total}</span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
          <motion.div className="h-full rounded-full" style={{ background: accent }} animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          <div className="mb-1 flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full text-[13px] font-bold text-white" style={{ background: accent }}>{step + 1}</span>
            <h3 className="font-display text-xl font-bold text-white">{current.title}</h3>
          </div>
          {current.subtitle && <p className="mb-5 ml-9 text-[13px] text-white/50">{current.subtitle}</p>}

          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {current.fields.map((f) => (
              <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
                <label className="mb-2 block text-[13px] font-medium text-white/80">
                  {f.label} {f.required && <span className="text-[#E11B22]">*</span>}
                </label>
                <Field field={f} value={values[f.name]} onChange={onChange} />
              </div>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Nav */}
      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={back}
          disabled={step === 0}
          data-testid="multistep-back-button"
          className="inline-flex items-center gap-2 rounded-[12px] border border-[color:var(--acx-border)] bg-white/5 px-5 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ArrowLeft size={16} /> Back
        </button>
        {step < total - 1 ? (
          <button
            type="button"
            onClick={next}
            data-testid="multistep-next-button"
            className="inline-flex items-center gap-2 rounded-[12px] bg-white px-6 py-3 text-[14px] font-semibold text-black transition-colors hover:bg-white/90"
          >
            Continue <ArrowRight size={16} />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="multistep-submit-button"
            className="inline-flex items-center gap-2 rounded-[12px] px-6 py-3 text-[14px] font-semibold text-white transition-colors disabled:opacity-60"
            style={{ background: accent }}
          >
            {submitting ? "Submitting..." : submitLabel} <ArrowRight size={16} />
          </button>
        )}
      </div>
      <p className="mt-4 text-center text-[12px] text-white/40">
        Your information is kept confidential and used only to evaluate this partnership.
      </p>
    </div>
  );
};

export default MultiStepForm;
